import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { sendMessage as sendChatMessage, ChatMessage, getChatStatus } from '../services/chat';

interface ChatContextType {
  messages: ChatMessage[];
  isOpen: boolean;
  isLoading: boolean;
  isConfigured: boolean;
  error: string | null;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  sendMessage: (message: string) => Promise<void>;
  clearMessages: () => void;
  checkStatus: () => Promise<void>;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const openChat = useCallback(() => setIsOpen(true), []);
  const closeChat = useCallback(() => setIsOpen(false), []);
  const toggleChat = useCallback(() => setIsOpen(prev => !prev), []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const checkStatus = useCallback(async () => {
    try {
      const status = await getChatStatus();
      setIsConfigured(status.enabled && status.configured);
    } catch (err) {
      console.error('Failed to check chat status:', err);
      setIsConfigured(false);
    }
  }, []);

  const sendMessage = useCallback(async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    setError(null);

    // Add user message immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Get conversation history (excluding current message)
      const history = messages.map(m => ({
        role: m.role,
        content: m.content,
      }));

      const response = await sendChatMessage({
        message: messageText,
        conversation_history: history,
      });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.message,
        timestamp: response.timestamp,
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message. Please try again.';
      setError(errorMessage);
      console.error('Chat error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [messages, isLoading]);

  const value: ChatContextType = {
    messages,
    isOpen,
    isLoading,
    isConfigured,
    error,
    openChat,
    closeChat,
    toggleChat,
    sendMessage,
    clearMessages,
    checkStatus,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
